import { authFetch, authHeaders, resolveApiBaseUrl } from '../api/auth'
import { useMemo, useState } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

const API_BASE_URL = resolveApiBaseUrl()

export default function CustomEmail({ embedded = false }) {
  const [csvFile, setCsvFile] = useState(null)
  const [manualEmails, setManualEmails] = useState('')
  const [subject, setSubject] = useState('')
  const [mode, setMode] = useState('html') // 'html' | 'text'
  const [content, setContent] = useState('')
  const [htmlView, setHtmlView] = useState('design') // 'design' | 'source'
  const [replyTo, setReplyTo] = useState('support@artstoheartsproject.com, Orders@artstoheartsproject.com')
  const [delayMs, setDelayMs] = useState(200)
  const [emailColumn, setEmailColumn] = useState('')
  const [dryRun, setDryRun] = useState(false)

  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  const approxPerSecond = useMemo(() => {
    if (!delayMs) return null
    return (1000 / delayMs).toFixed(2)
  }, [delayMs])

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }, { align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean'],
      ],
      clipboard: { matchVisual: false },
    }),
    [],
  )

  const quillFormats = useMemo(
    () => [
      'font',
      'size',
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'color',
      'background',
      'script',
      'list',
      'bullet',
      'indent',
      'direction',
      'align',
      'blockquote',
      'code-block',
      'link',
      'image',
    ],
    [],
  )

  const onSend = async () => {
    setError('')
    setResult(null)

    if (!csvFile && !manualEmails.trim()) {
      setError('CSV or manual emails required')
      return
    }
    if (!subject.trim()) {
      setError('Subject required')
      return
    }
    if (!content.trim()) {
      setError('Email content required')
      return
    }

    const form = new FormData()
    if (csvFile) form.append('file', csvFile)
    if (manualEmails.trim()) form.append('emails', manualEmails)
    form.append('subject', subject)
    form.append(
      'reply_to',
      replyTo.trim() || 'support@artstoheartsproject.com, Orders@artstoheartsproject.com'
    )
    form.append('delay_ms', String(delayMs ?? 200))
    form.append('dry_run', dryRun ? 'true' : 'false')
    if (emailColumn.trim()) form.append('email_column', emailColumn.trim())
    if (mode === 'html') form.append('html', content)
    else form.append('text', content)

    setSending(true)
    try {
      const resp = await authFetch(`${API_BASE_URL}/custom-email/send/`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        throw new Error(data?.error || `Request failed (${resp.status})`)
      }
      setResult(data)
    } catch (e) {
      setError(e?.message || 'Failed to send')
    } finally {
      setSending(false)
    }
  }

  return (
    <div
      style={{
        maxWidth: embedded ? 'none' : 980,
        margin: embedded ? 0 : '0 auto',
        padding: embedded ? 0 : '24px 16px',
      }}
    >
      <h2 style={{ margin: 0 }}>Custom Email (CSV + Manual Recipients)</h2>
      <p style={{ marginTop: 8, color: '#666' }}>
        Open this page directly at <code>/custom-email</code>. You can use CSV and/or paste multiple emails.
        CSV header should include a column like <code>email</code> or <code>customer_email</code>.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginTop: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Recipients</div>

            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>CSV Upload (optional)</div>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              />
            </label>

            <div style={{ height: 10 }} />

            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
                Email column (only if CSV is uploaded)
              </div>
              <input
                value={emailColumn}
                onChange={(e) => setEmailColumn(e.target.value)}
                placeholder="e.g. email or customer_email"
                style={{ width: '100%', padding: 10 }}
              />
            </label>

            <div style={{ height: 10 }} />

            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
                Manual emails (optional)
              </div>
              <textarea
                value={manualEmails}
                onChange={(e) => setManualEmails(e.target.value)}
                placeholder="Paste emails separated by commas or new lines"
                style={{ width: '100%', minHeight: 100, padding: 10 }}
              />
            </label>
          </div>

          <label>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
              Subject
            </div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              style={{ width: '100%', padding: 10 }}
            />
          </label>

          <label>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
              Reply-To (optional)
            </div>
            <input
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder="support@artstoheartsproject.com, Orders@artstoheartsproject.com"
              style={{ width: '100%', padding: 10 }}
            />
          </label>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="radio"
                name="mode"
                checked={mode === 'html'}
                onChange={() => setMode('html')}
              />
              HTML
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="radio"
                name="mode"
                checked={mode === 'text'}
                onChange={() => setMode('text')}
              />
              Text
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Dry-run (no send)
            </label>
          </div>

          <label>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
              Rate limit delay (ms) — approx/s:{' '}
              <strong>{approxPerSecond ?? '∞'}</strong>
            </div>
            <input
              type="number"
              min="0"
              max="60000"
              value={delayMs}
              onChange={(e) => setDelayMs(Number(e.target.value))}
              style={{ width: 180, padding: 10 }}
            />
          </label>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 12, flex: 1 }}>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 10 }}>
              Content ({mode.toUpperCase()})
            </div>

            {mode === 'text' ? (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write email text..."
                style={{ width: '100%', minHeight: 240, padding: 10 }}
              />
            ) : (
              <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setHtmlView('design')}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      background: htmlView === 'design' ? '#111' : '#fff',
                      color: htmlView === 'design' ? '#fff' : '#111',
                      cursor: 'pointer',
                    }}
                  >
                    Design
                  </button>
                  <button
                    type="button"
                    onClick={() => setHtmlView('source')}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      background: htmlView === 'source' ? '#111' : '#fff',
                      color: htmlView === 'source' ? '#fff' : '#111',
                      cursor: 'pointer',
                    }}
                  >
                    HTML source
                  </button>
                </div>

                {htmlView === 'source' ? (
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="<h2>Hello</h2><p>Write your email…</p>"
                    style={{
                      width: '100%',
                      minHeight: 340,
                      padding: 10,
                      border: '1px solid #ddd',
                      borderRadius: 10,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: 13,
                    }}
                  />
                ) : (
                  <div style={{ border: '1px solid #ddd', borderRadius: 10, overflow: 'hidden' }}>
                    <ReactQuill
                      theme="snow"
                      value={content}
                      onChange={setContent}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Write your email..."
                      style={{ background: '#fff' }}
                    />
                    <style>{`
                      .ql-toolbar.ql-snow { border: none; border-bottom: 1px solid #eee; }
                      .ql-container.ql-snow { border: none; }
                      .ql-editor { min-height: 300px; font-size: 14px; line-height: 1.55; }
                      .ql-editor p { margin: 0 0 10px; }
                    `}</style>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() =>
                      setContent(
                        '<h2>Hello!</h2><p>Write your message here.</p><p>Regards,<br/>Arts To Hearts Project</p>',
                      )
                    }
                    style={{ padding: '8px 10px' }}
                  >
                    Insert template
                  </button>
                  <button type="button" onClick={() => setContent('')} style={{ padding: '8px 10px' }}>
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={onSend}
            disabled={sending}
            style={{
              padding: '12px 14px',
              background: sending ? '#999' : '#111',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: sending ? 'not-allowed' : 'pointer',
            }}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>

          {error ? (
            <div style={{ color: '#b00020' }}>
              <strong>Error:</strong> {error}
            </div>
          ) : null}
        </div>
      </div>

      {result ? (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ marginBottom: 8 }}>Result</h3>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, color: '#555' }}>Valid recipients</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {result?.stats?.total_valid_recipients ?? '-'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#555' }}>Sent</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {result?.stats?.sent ?? '-'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#555' }}>Failed</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {result?.stats?.failed ?? '-'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#555' }}>Invalid rows</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {result?.stats?.invalid_rows ?? '-'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#555' }}>Delay (ms)</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {result?.rate_limit?.delay_ms ?? '-'}
              </div>
            </div>
          </div>

          {Array.isArray(result?.failures) && result.failures.length ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
                Failures (showing {result.failures.length}
                {result.failures_truncated ? `, +${result.failures_truncated} more` : ''}
                )
              </div>
              <div style={{ border: '1px solid #eee', borderRadius: 8 }}>
                <pre style={{ margin: 0, padding: 12, overflow: 'auto', maxHeight: 260 }}>
{JSON.stringify(result.failures, null, 2)}
                </pre>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

